import { format, isValid, parseISO } from 'date-fns';

const DAY_MS = 24 * 60 * 60 * 1000;

export const RIGHT_TO_WORK_LIST_OPTIONS = [
  { value: 'list_a', label: 'List A - Unlimited right to work' },
  { value: 'list_b', label: 'List B - Time-limited right to work' },
];

export const RIGHT_TO_WORK_CHECK_METHOD_OPTIONS = [
  { value: 'online_right_to_work_check', label: 'Online Right to Work Check' },
  { value: 'digital_identity_service', label: 'Registered digital identity service (British / Irish passport)' },
  { value: 'manual_document_check', label: 'Manual Document Check' },
  { value: 'home_office_employer_checking_service', label: 'Home Office Employer Checking Service' },
];

const LIST_A_DOCUMENT_OPTIONS = [
  { value: 'uk_irish_passport', label: 'UK/Irish passport (current or expired)' },
  { value: 'british_citizenship_certificate', label: 'Certificate of registration/naturalisation as a British citizen' },
  { value: 'home_office_online_check_unlimited', label: 'Home Office online check - unlimited right to work' },
  { value: 'eu_settled_status', label: 'EU Settlement Scheme - Settled Status (online check)' },
];

const LIST_B_DOCUMENT_OPTIONS = [
  { value: 'home_office_online_check_time_limited', label: 'Home Office online check - time-limited right to work' },
  { value: 'current_passport_with_visa', label: 'Current passport with valid visa endorsement' },
  { value: 'biometric_residence_permit', label: 'Biometric Residence Permit (BRP) - not accepted as current Right to Work evidence' },
  { value: 'pre_settled_status', label: 'EU Settlement Scheme - Pre-Settled Status' },
  { value: 'certificate_of_application', label: 'Certificate of Application under EU Settlement Scheme + Positive Verification Notice' },
  { value: 'application_registration_card', label: 'Application Registration Card + Positive Verification Notice' },
  { value: 'positive_verification_notice', label: 'Positive Verification Notice from the Employer Checking Service' },
];

export const RIGHT_TO_WORK_DOCUMENT_OPTIONS = {
  list_a: LIST_A_DOCUMENT_OPTIONS,
  list_b: LIST_B_DOCUMENT_OPTIONS,
};

export const RIGHT_TO_WORK_DOCUMENT_CHECKLIST_OPTIONS = [
  {
    type: 'group',
    key: 'list_a',
    label: 'List A - Unlimited Right to Work (no follow-up check required)',
  },
  ...LIST_A_DOCUMENT_OPTIONS,
  {
    type: 'group',
    key: 'list_b',
    label: 'Time-limited evidence (follow-up depends on the official check)',
  },
  ...LIST_B_DOCUMENT_OPTIONS,
];

const toUtcMidnight = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(Date.UTC(
      value.getUTCFullYear(),
      value.getUTCMonth(),
      value.getUTCDate(),
    ));
  }

  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (match) {
      const [, year, month, day] = match.map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
      if (value.trim().length === 10) return date;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Date(Date.UTC(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
  ));
};

const toDateOnlyString = (value) => {
  const date = toUtcMidnight(value);
  if (!date) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeIsoDateOnly = (value) => {
  const normalized = String(value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return '';

  const parsed = parseISO(normalized);
  if (!isValid(parsed) || format(parsed, 'yyyy-MM-dd') !== normalized) return '';
  return normalized;
};

// Expired and Expiring soon rows need a follow-up check now; the badge counts them.
const RIGHT_TO_WORK_ATTENTION_STATUSES = new Set(['expired', 'expiring_soon']);

export const isRightToWorkAttentionRow = (row = {}) =>
  row?.excluded !== true
  && row?.workPermissionsExcluded !== true
  && RIGHT_TO_WORK_ATTENTION_STATUSES.has(row?.rightToWorkStatus);

export const countRightToWorkAttentionRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter(isRightToWorkAttentionRow).length;

const normalizeListType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'list_a' || normalized === 'list_b') return normalized;
  return '';
};

// Follow-up deadlines come from the official result, never from a recurring check-date cycle.
export const getRightToWorkFollowUpBasis = (record = {}) => {
  if (record?.rightToWorkCheckMethod === 'home_office_employer_checking_service') return 'ecs_notice';
  if (normalizeListType(record?.rightToWorkListType) === 'list_a') return 'not_required';
  return record?.rightToWorkFollowUpBasis || 'permission_expiry';
};

const hasEussNoRepeatCheck = (record = {}) =>
  getRightToWorkFollowUpBasis(record) === 'euss_continuous'
  && record?.rightToWorkCheckMethod === 'online_right_to_work_check';

export const isRightToWorkRepeatCheckRequired = (record = {}) =>
  getRightToWorkFollowUpBasis(record) !== 'not_required' && !hasEussNoRepeatCheck(record);

export const deriveRightToWorkCheckDeadline = (record = {}) => {
  const basis = getRightToWorkFollowUpBasis(record);
  if (!isRightToWorkRepeatCheckRequired(record)) return null;
  if (basis === 'ecs_notice') {
    // Enter the validity end stated by the ECS/PVN. Do not grant six months from an upload/date edit.
    return toDateOnlyString(record?.rightToWorkPvnValidUntil);
  }
  if (basis !== 'permission_expiry') return null;
  return toDateOnlyString(record?.rightToWorkExpiryDate ?? record?.rightToWorkPermissionExpiryDate);
};

// The Additional info checklist is informational; status uses the official RTW details.
const hasRightToWorkCheckDetails = (record = {}) => Boolean(
  normalizeIsoDateOnly(record?.rightToWorkCheckDate)
  && String(record?.rightToWorkDocumentReference ?? '').trim(),
);

export const rightToWorkToday = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
};

const buildDiffDays = (targetDate, referenceDate = new Date()) => {
  const target = toUtcMidnight(targetDate);
  if (!target) return null;

  const dateOnly = typeof referenceDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(referenceDate);
  const reference = toUtcMidnight(dateOnly ? referenceDate : rightToWorkToday(referenceDate)) || toUtcMidnight(rightToWorkToday());
  return Math.floor((target.getTime() - reference.getTime()) / DAY_MS);
};

export const deriveRightToWorkStatus = (record = {}, referenceDate = new Date()) => {
  const basis = getRightToWorkFollowUpBasis(record);
  if (basis === 'ecs_notice' && (!hasRightToWorkCheckDetails(record) || buildDiffDays(record.rightToWorkCheckDate, referenceDate) > 0)) return 'missing';
  if (basis === 'euss_continuous' && !hasEussNoRepeatCheck(record)) return 'missing';
  if (basis === 'not_required' || hasEussNoRepeatCheck(record)) {
    return hasRightToWorkCheckDetails(record)
      && buildDiffDays(record.rightToWorkCheckDate, referenceDate) <= 0 ? 'valid' : 'missing';
  }

  const expiry = deriveRightToWorkCheckDeadline(record);
  const diffDays = buildDiffDays(expiry, referenceDate);
  if (diffDays === null) return 'missing';
  const fromValue = basis === 'ecs_notice' ? record?.rightToWorkCheckDate : record?.rightToWorkDocumentDate;
  const fromDate = toUtcMidnight(fromValue);
  const expiryDate = toUtcMidnight(expiry);
  if (fromValue && (!fromDate || fromDate > expiryDate)) return 'missing';
  if (fromDate && buildDiffDays(fromDate, referenceDate) > 0) return 'not_started';
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'expiring_soon';
  return 'valid';
};

export const getRightToWorkStatusHint = (record = {}, referenceDate = new Date()) => {
  const status = deriveRightToWorkStatus(record, referenceDate);
  if (status === 'not_started') return 'The Right to work from date is in the future.';
  if (status !== 'missing') return '';
  const basis = getRightToWorkFollowUpBasis(record);
  if (basis === 'ecs_notice') return 'Complete the ECS check date, reference and PVN valid until date from the notice.';
  if (basis === 'euss_continuous') return 'Record the employer online check date and reference confirming granted EUSS status for this employment.';
  const listType = normalizeListType(record?.rightToWorkListType);
  if (listType === 'list_a') {
    if (buildDiffDays(record?.rightToWorkCheckDate, referenceDate) > 0) return 'Date of Check cannot be in the future.';
    return 'Complete Date of Check and Document reference number in Right to Work. A From date is not required for unlimited right to work.';
  }
  if (listType === 'list_b') {
    if (record?.rightToWorkDocumentDate && toUtcMidnight(record?.rightToWorkExpiryDate)) return 'Check the Right to work from date: it must be valid and no later than Right to work until.';
    return 'The Right to work until date is missing or invalid.';
  }
  return 'Select a Right to Work List and complete its verification details.';
};

export const formatRightToWorkStatusLabel = (value) => {
  switch (String(value || '').trim()) {
    case 'engagement_ended':
      return 'Engagement ended';
    case 'needs_verification':
      return 'Needs verification';
    case 'valid':
      return 'Valid';
    case 'not_started':
      return 'Not yet valid';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'expired':
      return 'Expired';
    case 'missing':
    default:
      return 'Missing';
  }
};

export const formatRightToWorkListLabel = (value) => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'list_a':
      return 'List A';
    case 'list_b':
      return 'List B';
    default:
      return '—';
  }
};

export const getRightToWorkDocumentOptions = (listType) =>
  RIGHT_TO_WORK_DOCUMENT_OPTIONS[normalizeListType(listType)] || [];

export const getRightToWorkDocumentChecklistOptions = () =>
  RIGHT_TO_WORK_DOCUMENT_CHECKLIST_OPTIONS;
